/**
 * The words the refund console speaks.
 *
 * Kept apart from the screen because this is the part that has to be right for
 * someone who did not build the system. A status like `processing` is only
 * meaningful if you already know that the wallet is debited first and the bank
 * settles days later; spelled out, it stops being knowledge you have to have
 * and becomes something the page tells you.
 *
 * Every status carries three things: what it is, what it means for the
 * customer's money, and what -- if anything -- the person reading is expected
 * to do about it.
 */
import React from "react";

export const inr = (n: number | string | null | undefined) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// The API stores UTC but serialises it naive -- "2026-09-04T05:32:43" with no Z
// and no offset. JS parses a naive ISO string as LOCAL time, so toLocaleString
// converts nothing and renders 05:32 against an 11:02 IST clock: plausible, and
// five and a half hours wrong. Stamp UTC when the string carries no zone, then
// let the browser convert to wherever the viewer actually is.
export const asUtc = (s: string) =>
  /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;

export const dt = (s: string | null | undefined) =>
  s
    ? new Date(asUtc(s)).toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

export const time = (s: string | null | undefined) =>
  s
    ? new Date(asUtc(s)).toLocaleTimeString(undefined, {
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

/** "4 minutes ago", "2 days ago" -- how long someone has been waiting. */
export const ago = (s: string | null | undefined): string => {
  if (!s) return "—";
  const secs = (Date.now() - new Date(asUtc(s)).getTime()) / 1000;
  if (secs < 90) return "just now";
  const units: Array<[number, string]> = [
    [60, "minute"], [3600, "hour"], [86400, "day"], [604800, "week"],
  ];
  let out = "a while";
  for (let i = units.length - 1; i >= 0; i -= 1) {
    const [size, name] = units[i];
    if (secs >= size) {
      const n = Math.floor(secs / size);
      out = `${n} ${name}${n > 1 ? "s" : ""} ago`;
      break;
    }
  }
  return out;
};

export interface StatusStory {
  label: string;
  tone: "ok" | "bad" | "warn" | "muted" | "info";
  /** What has actually happened to the money. */
  meaning: string;
  /** What the person reading has to do, if anything. */
  next: string;
  /** True when a human is the thing standing between a customer and their money. */
  needsSomeone: boolean;
}

export const statusStory = (status: string): StatusStory => {
  switch (status) {
    case "pending_review":
      return {
        label: "Waiting for your decision",
        tone: "warn",
        meaning:
          "Nothing has moved. The customer has been told we are checking it.",
        next: "Approve it or decline it — nothing happens until you do.",
        needsSomeone: true,
      };
    case "processing":
      return {
        label: "On its way to the customer",
        tone: "info",
        meaning:
          "The wallet is already debited and Cashfree has the payout. Banks take 7–10 working days.",
        next:
          "Nothing to do. We ask Cashfree every ten minutes and email you when it lands.",
        needsSomeone: false,
      };
    case "succeeded":
      return {
        label: "Paid",
        tone: "ok",
        meaning: "Cashfree confirms the money reached the customer.",
        next:
          "If they say they cannot see it, quote the bank reference below — that is the number their bank can trace.",
        needsSomeone: false,
      };
    case "failed":
      return {
        label: "Cashfree rejected it",
        tone: "bad",
        meaning:
          "The wallet is still debited and the customer has NOT been paid. Money is never returned to the wallet automatically — that is a person's decision, not a job's.",
        next:
          "Someone has to act: raise the payout again, or credit the wallet by hand.",
        needsSomeone: true,
      };
    case "rejected":
      return {
        label: "You declined it",
        tone: "muted",
        meaning: "Nothing moved. The customer keeps the balance they had.",
        next: "Nothing to do.",
        needsSomeone: false,
      };
    default:
      return {
        label: status,
        tone: "muted",
        meaning: "This state is not one the console knows about.",
        next: "Open the history below and check with Cashfree before acting.",
        needsSomeone: true,
      };
  }
};

/**
 * One audit row as a sentence.
 *
 * The audit table is the truthful record, but read as a table -- actor,
 * actor_type, action, from_status, to_status -- it is a database dump. The same
 * rows read in order are the story of what happened to someone's money, which
 * is what a person opening this actually wants.
 */
export const auditSentence = (a: {
  actor: string; actor_type: string; action: string;
  to_status: string | null; detail: any;
}): { text: React.ReactNode; tone: StatusStory["tone"] } => {
  const d = (typeof a.detail === "string" ? safeParse(a.detail) : a.detail) ?? {};
  const who = a.actor || "someone";
  switch (a.action) {
    case "raised":
      return a.actor_type === "user"
        ? { text: <><b>They asked</b> for their money back, from the app</>, tone: "info" }
        : { text: <><b>{who} raised it</b> on the customer's behalf</>, tone: "info" };
    case "review_notice_sent":
      return {
        text: <><b>Reviewer emailed</b>{d.to?.length ? ` — ${d.to.join(", ")}` : ""}</>,
        tone: "muted",
      };
    case "approved":
      return { text: <><b>{who} approved it</b></>, tone: "ok" };
    case "rejected":
      return {
        text: <><b>{who} declined it</b>{d.note ? ` — ${d.note}` : ""}</>,
        tone: "muted",
      };
    case "gateway_accepted":
      return {
        text: <><b>Cashfree accepted it</b>{d.refund_id ? ` — ${d.refund_id}` : ""}</>,
        tone: "ok",
      };
    case "gateway_error":
      return {
        text: <><b>Cashfree would not take it</b>{d.error ? ` — ${d.error}` : ""}</>,
        tone: "bad",
      };
    case "settled":
      return a.to_status === "succeeded"
        ? { text: <><b>Cashfree paid the customer</b></>, tone: "ok" }
        : {
            text: <><b>Cashfree rejected the payout</b>{d.failure_detail ? ` — ${d.failure_detail}` : ""}</>,
            tone: "bad",
          };
    case "approve_failed":
      return {
        text: <><b>Approval could not be sent</b>{d.error ? ` — ${d.error}` : ""}</>,
        tone: "bad",
      };
    default:
      return {
        text: <>{a.action.replace(/_/g, " ")}{who ? ` — ${who}` : ""}</>,
        tone: "muted",
      };
  }
};

const safeParse = (s: string) => {
  try { return JSON.parse(s); } catch { return {}; }
};

/* ------------------------------------------------- what the customer gets */

/**
 * The refund, written for the person who is owed the money.
 *
 * Support answers this question by hand today, which means the answer is
 * slightly different every time and sometimes contains things a customer should
 * not be reading -- our internal reference, a wallet id, the gateway's status
 * word. This says the same thing every time, in the order a worried person
 * reads it: what we did, where it went, the number their bank can trace, and
 * when to worry.
 *
 * The bank reference is the whole point. It is the only identifier in the
 * system that means anything to the customer's bank, so it leads once it
 * exists and its absence is explained when it does not.
 */
export interface CustomerCopy {
  subject: string;
  body: string;
  /** False when there is nothing honest to tell them yet. */
  sendable: boolean;
}

export const customerMessage = (d: {
  status: string; amount: number; clawback_amount?: number;
  who?: string | null; upi_id?: string | null; payment_group?: string | null;
  refund_arn?: string | null; settled_at?: string | null; created_at?: string;
  lot_paid_at?: string | null;
}): CustomerCopy => {
  const name = (d.who ?? "").trim().split(/\s+/)[0] || "there";
  const where = d.upi_id || d.payment_group || "the method you paid with";
  const amt = inr(d.amount);
  const sign = "\n\n— ChargingAdda";

  switch (d.status) {
    case "succeeded":
      return {
        sendable: true,
        subject: `Your ${amt} refund has been paid`,
        body:
          `Hi ${name},\n\n` +
          `We have refunded ${amt} to ${where} — the same way you paid.\n\n` +
          (d.refund_arn
            ? `Bank reference: ${d.refund_arn}\n`
            : "") +
          `Refunded on: ${dt(d.settled_at)}\n\n` +
          `Banks usually take 7–10 working days to show a refund. If you cannot ` +
          `see it after that, ${d.refund_arn
            ? "give your bank the reference above and they can trace it."
            : "reply to this message and we will get the bank reference for you."}` +
          sign,
      };
    case "processing":
      return {
        sendable: true,
        subject: `Your ${amt} refund is on its way`,
        body:
          `Hi ${name},\n\n` +
          `Your refund of ${amt} has been sent back to ${where} — the same way ` +
          `you paid.\n\n` +
          `Banks take 7–10 working days to show it. We will confirm once your ` +
          `bank has it, and we will send you a reference you can trace it with.` +
          sign,
      };
    case "pending_review":
      return {
        sendable: true,
        subject: `We have your refund request`,
        body:
          `Hi ${name},\n\n` +
          `We have your request to refund ${amt}. Our team checks each one ` +
          `before the money is sent, and we will write to you as soon as it is ` +
          `on its way.\n\n` +
          `The money goes back the same way you paid it.` +
          sign,
      };
    case "rejected":
      return {
        sendable: false,
        subject: `About your ${amt} refund request`,
        body:
          `Hi ${name},\n\n` +
          `We were not able to process your refund request for ${amt}, and your ` +
          `wallet balance is unchanged.\n\n` +
          `WRITE THE REASON HERE BEFORE SENDING — the internal note is not ` +
          `something to paste to a customer.` +
          sign,
      };
    default:
      return {
        sendable: false,
        subject: `About your ${amt} refund`,
        body:
          `This refund has not reached a state worth writing to the customer ` +
          `about yet. Check with Cashfree first.`,
      };
  }
};

/**
 * The same thing as a picture, for support channels that eat plain text.
 *
 * Drawn on a canvas rather than screenshotting the DOM: it needs no dependency,
 * renders identically wherever it runs, and -- the point -- it contains only
 * the fields chosen here, so an internal reference cannot leak into an image
 * that gets forwarded.
 */
export const drawReceipt = (d: {
  status: string; amount: number; who?: string | null;
  upi_id?: string | null; payment_group?: string | null;
  refund_arn?: string | null; settled_at?: string | null;
}): Promise<Blob | null> => {
  const S = 2; // draw at 2x so it stays sharp when someone zooms in
  const W = 620;
  const H = 380;
  const c = document.createElement("canvas");
  c.width = W * S;
  c.height = H * S;
  const x = c.getContext("2d");
  if (!x) return Promise.resolve(null);
  x.scale(S, S);

  const paid = d.status === "succeeded";
  const ink = "#101828";
  const dim = "#667085";

  x.fillStyle = "#ffffff";
  x.fillRect(0, 0, W, H);
  x.fillStyle = paid ? "#027a48" : "#7f56d9";
  x.fillRect(0, 0, W, 6);

  x.fillStyle = ink;
  x.font = "600 20px system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
  x.fillText("ChargingAdda", 36, 56);
  x.fillStyle = dim;
  x.font = "13px system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
  x.fillText(paid ? "Refund confirmation" : "Refund in progress", 36, 78);

  x.fillStyle = ink;
  x.font = "700 40px system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
  x.fillText(inr(d.amount), 36, 138);
  x.fillStyle = dim;
  x.font = "13px system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
  x.fillText(
    paid ? "refunded to the way you paid" : "on its way back to the way you paid",
    36, 160,
  );

  const rows: Array<[string, string]> = [
    ["Sent to", (d.upi_id || d.payment_group || "your original payment method").slice(0, 44)],
    ...(d.refund_arn ? ([["Bank reference", d.refund_arn]] as Array<[string, string]>) : []),
    ...(paid && d.settled_at ? ([["Refunded on", dt(d.settled_at)]] as Array<[string, string]>) : []),
  ];
  let y = 200;
  rows.forEach(([k, v]) => {
    x.fillStyle = "#f2f4f7";
    x.fillRect(36, y - 2, W - 72, 1);
    y += 26;
    x.fillStyle = dim;
    x.font = "13px system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
    x.fillText(k, 36, y);
    x.fillStyle = ink;
    x.font = "600 13px system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
    x.textAlign = "right";
    x.fillText(v, W - 36, y);
    x.textAlign = "left";
    y += 14;
  });

  x.fillStyle = "#f9fafb";
  x.fillRect(36, H - 92, W - 72, 56);
  x.fillStyle = dim;
  x.font = "12.5px system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
  x.fillText("Banks take 7–10 working days to show a refund.", 52, H - 68);
  x.fillText(
    d.refund_arn
      ? "Your bank can trace it with the reference above."
      : "We will send you a reference once your bank has it.",
    52, H - 50,
  );

  return new Promise((resolve) => c.toBlob((b) => resolve(b), "image/png"));
};

/**
 * The four things that happen to a refund, and where this one has got to.
 *
 * An operator-raised refund skips the decision -- the operator already made it
 * -- so it is a three-step journey and pretending otherwise would show a step
 * that can never be reached.
 */
export const journeyOf = (status: string, raisedByUser: boolean) => {
  const steps = raisedByUser
    ? ["Asked", "Approved", "Sent to Cashfree", "Paid"]
    : ["Raised", "Sent to Cashfree", "Paid"];
  // `at` is the step still to happen, so everything before it renders as done.
  // A settled refund is past the end -- steps.length, not the last index --
  // otherwise the final step draws as "in progress" and a refund that has been
  // paid looks like one that has not.
  const last = steps.length - 1;
  const decision = raisedByUser ? 1 : 0;
  switch (status) {
    case "pending_review": return { steps, at: decision, broken: false };
    case "processing": return { steps, at: last, broken: false };
    case "succeeded": return { steps, at: steps.length, broken: false };
    case "failed": return { steps, at: last, broken: true };
    case "rejected": return { steps, at: decision, broken: true };
    default: return { steps, at: 0, broken: false };
  }
};
